import React, { useCallback, useLayoutEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button, Card, IconButton, TextField } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { DialogProps } from '@mui/material/Dialog';

import { IAppState } from '../../../../store/rootDuck';
import { setLayoutFooter, setLayoutSubheader } from '../../../../utils/layout';
import { useDefineUserRole, useForbidAccess, useFormatMessage } from '../../../../hooks';
import Preloader from '../../../../components/ui/Preloader/Preloader';
import homeStyles from '../../homeStyles';
import { getRole } from './utils';
import {
  BUYER_ROLE,
  BUYER_STAFF_ROLE,
  UserListType,
  VENDOR_ROLE,
  VENDOR_STAFF_ROLE,
} from '../constants';
import { useStylesEditPage } from './hooks/useStyleEditPage';
import { actions as companiesActions } from '../../../../store/ducks/companies.duck';
import { actions as categoriesActions } from '../../../../store/ducks/categories.duck';
import { Modal } from '../../../../components/other/Modals';
import CompaniesTable from '../../../../components/tableComponents/Table/CompaniesTable';
import { IUser } from '../../../../interfaces/user';
import CompanyBind from '../../companies/CompanyBind';
import ButtonWithLoader from '../../../../components/other/Buttons/ButtonWithLoader';
import { useCreateInvite } from './hooks/useCreateInvite';
import { useFormikUserInvite } from './hooks/useFormikUserInvite';
import { needBindCompany } from '../../../../utils/user';

const UserInvite = () => {
  const { type } = useParams();
  const role = getRole(type as UserListType);
  const navigate = useNavigate();
  const homeClasses = homeStyles();
  const dispatch = useDispatch();
  const { me, loadingMe } = useSelector(
    ({ profile: { me, loading } }: IAppState) => ({ me, loadingMe: loading }),
    shallowEqual
  );
  const isVendor = useDefineUserRole(me, 'ROLE_VENDOR');
  const isBuyer = useDefineUserRole(me, 'ROLE_BUYER');
  const location = useLocation();

  const { companies, loadingCompanies, pageCompanies, perPageCompanies, totalCompanies } =
    useSelector(
      ({ companies: { companies, loading, page, per_page, total } }: IAppState) => ({
        companies,
        loadingCompanies: loading,
        pageCompanies: page,
        perPageCompanies: per_page,
        totalCompanies: total,
      }),
      shallowEqual
    );
  const [openDialogCompanies, setOpenDialogCompanies] = useState(false);
  const fm = useFormatMessage();
  const classes = useStylesEditPage();

  const { fetchInvite, loadingInvite, successInvite } = useCreateInvite();

  const formik = useFormikUserInvite(role, fetchInvite, me, location?.state?.company);

  const { values, errors, handleChange, handleBlur, handleSubmit, setFieldValue } = formik;

  const roleCompany: Record<any, any> = {
    [VENDOR_ROLE]: VENDOR_ROLE,
    [VENDOR_STAFF_ROLE]: VENDOR_ROLE,
    [BUYER_ROLE]: BUYER_ROLE,
    [BUYER_STAFF_ROLE]: BUYER_ROLE,
  };

  const openCompaniesDialog = useCallback(() => {
    dispatch(
      companiesActions.fetchRequest({
        perPage: 20,
        page: 1,
        roles: roleCompany[role],
      })
    );
    setOpenDialogCompanies(true);
  }, [values, roleCompany]);

  useLayoutEffect(() => {
    dispatch(categoriesActions.fetchFullRequest());
  }, []);

  useLayoutEffect(() => {
    successInvite && navigate(-1);
  }, [successInvite]);

  useForbidAccess(me, ['ROLE_MANAGER']);

  setLayoutSubheader({
    title: fm('INVITE'),
  });

  setLayoutFooter({ show: true });

  if (me && !me.is_admin && !isVendor && !isBuyer && needBindCompany(me))
    return <CompanyBind />;

  if (loadingMe) return <Preloader />;

  return (
    <>
      <Card className={homeClasses.classes.container}>
        <div className={homeClasses.classes.form}>
          {me?.is_admin && (
            <div className={classes.companyField}>
              <TextField
                label={fm('COMPANY.FORM.COMPANY')}
                margin='normal'
                name='company'
                placeholder={fm('COMPANY.FORM.COMPANY')}
                value={
                  values?.company?.name || values?.company?.brand || values?.company?.id || ''
                }
                variant='outlined'
                error={Boolean(errors.company)}
                InputProps={{
                  readOnly: true,
                  endAdornment: values?.company && (
                    <IconButton
                      onClick={() => {
                        setFieldValue('company', null);
                      }}
                    >
                      <DeleteIcon fontSize='small' />
                    </IconButton>
                  ),
                }}
              />
              <Button
                className={classes.allCompaniesBtn}
                style={{
                  marginTop: 8,
                  marginLeft: 10,
                  height: 52,
                }}
                variant='outlined'
                color='primary'
                onClick={openCompaniesDialog}
              >
                {fm('COMPANY.BUTTON.ALL')}
              </Button>
            </div>
          )}
          <TextField
            type='text'
            label={fm('USER.EDIT.EMAIL')}
            margin='normal'
            name='email'
            value={values.email}
            variant='outlined'
            onBlur={handleBlur}
            onChange={handleChange}
            helperText={errors.email}
            error={Boolean(errors.email)}
          />
          <div className={classes.actions}>
            <Button
              onClick={() => navigate(-1)}
              className={classes.buttons}
              variant='contained'
              color='success'
            >
              {fm('COMMON.BUTTON.BACK')}
            </Button>
            <ButtonWithLoader
              onPress={handleSubmit}
              marginLeft={10}
              loading={loadingInvite}
              disabled={loadingInvite}
            >
              {fm('COMMON.BUTTON.CREATE')}
            </ButtonWithLoader>
          </div>
        </div>
      </Card>

      <Modal
        DialogProps={{ maxWidth: 'md' } as DialogProps}
        open={openDialogCompanies}
        onClose={() => setOpenDialogCompanies(false)}
        title={fm('COMPANY.EDIT.MOUNT_TITLE')}
        loading={loadingCompanies}
        content={
          <CompaniesTable
            me={me as IUser}
            page={pageCompanies}
            perPage={perPageCompanies}
            companySelect={values?.company}
            total={totalCompanies}
            onConnectUser={company => {
              setFieldValue('company', company);
            }}
            companies={companies}
          />
        }
        actions={[
          { title: fm('COMMON.BUTTON.CLOSE'), onClick: () => setOpenDialogCompanies(false) },
        ]}
      />
    </>
  );
};

export default UserInvite;
